import { type VariantProps, cva } from "class-variance-authority";
import { type ButtonHTMLAttributes, forwardRef } from "react";
import { cn } from "../cn";

// Variant styles live in a plain record (not inline in the cva call) so
// other button-like controls — e.g. the Combobox trigger — can reuse the
// same color tokens without inheriting Button's layout (centering, active
// scale, icon sizing).
export const buttonVariantStyles = {
  primary:
    "bg-primary text-primary-foreground shadow-sm hover:shadow-glow hover:brightness-[1.04]",
  secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/70",
  outline:
    "border border-border bg-surface text-foreground hover:border-foreground/20 hover:bg-muted/60",
  ghost: "text-foreground hover:bg-muted/70",
  subtle: "bg-primary-soft text-accent-foreground hover:bg-accent",
  destructive:
    "bg-destructive text-destructive-foreground shadow-sm hover:brightness-[1.05]",
} as const;

export type ButtonVariant = keyof typeof buttonVariantStyles;

const buttonVariants = cva(
  // cursor-pointer on disabled buttons is a no-op: the global base rule in
  // globals.css already excludes `:disabled`, and `disabled:pointer-events-none`
  // below stops hover/click from reaching a disabled button anyway.
  "inline-flex select-none items-center justify-center gap-2 whitespace-nowrap rounded-xl font-semibold transition-[transform,background-color,box-shadow,color,border-color] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background active:scale-[0.97] disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-[1.1em] [&_svg]:shrink-0",
  {
    variants: {
      variant: buttonVariantStyles,
      size: {
        sm: "h-9 px-4 text-sm",
        md: "h-11 px-5 text-sm",
        lg: "h-12 px-7 text-base",
        icon: "size-10",
        "icon-sm": "size-9",
      },
    },
    defaultVariants: { variant: "primary", size: "md" },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, type = "button", ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  ),
);
Button.displayName = "Button";

export { buttonVariants };
